/**
 * Auth Context (Stub)
 *
 * Minimal auth context for backwards compatibility.
 * OAuth has been removed - TES authentication is now handled server-side via API key.
 */

import React, { createContext, useContext, ReactNode, useMemo } from 'react';

// User info (not used without OAuth, but kept for type compatibility)
export interface TESUser {
  id: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
}

// Auth state
interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: TESUser | null;
  error: string | null;
}

// Context type
interface AuthContextType {
  state: AuthState;
  login: (redirectUri?: string) => Promise<void>;
  handleCallback: (code: string, state: string, redirectUri: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  clearError: () => void;
}

// Default state - always unauthenticated (auth handled server-side)
const defaultState: AuthState = {
  isAuthenticated: false,
  isLoading: false,
  user: null,
  error: null,
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const value = useMemo(() => ({
    state: defaultState,
    login: async () => {
      console.warn('[Auth] OAuth login has been removed. Authentication is handled server-side.');
    },
    handleCallback: async () => {
      console.warn('[Auth] OAuth callback has been removed. Authentication is handled server-side.');
    },
    logout: async () => {
      console.log('[Auth] Logout called (no-op without OAuth)');
    },
    refreshToken: async () => false,
    clearError: () => {},
  }), []);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper hook for getting just auth status
export function useIsAuthenticated(): boolean {
  const { state } = useAuth();
  return state.isAuthenticated;
}

// Helper hook for getting current user
export function useCurrentUser(): TESUser | null {
  const { state } = useAuth();
  return state.user;
}

export default AuthContext;
